import { Deferrable } from '@canvas/deferrable';

import {
  type Command,
  CreateEntityCommand,
  type BaseCommandProperties,
} from './command-definition';
import { CommandQueue } from './command-queue';
import {
  throwExistingSubscriberDevelopmentError,
  throwNoSubscriptionsDevelopmentError,
} from './command-service-development-errors';
import {
  type CommandHandlerWithOptions,
  type CommandConstructor,
  type SubscriberCallbackName,
  type SubscriberName,
  type SendHook,
  type ValidateHook,
  type CheckIsCreateDisabledHook,
  type SubscriptionOptions,
  type CommandHandlerWithData,
  type CommandHandler,
} from './command-service-types';

export type CommandServiceDebugger = (name: string) => void;
export type CommandServiceExecuteDebugger = (name: string, handler: string) => void;

export class CommandService {
  private subscriptions: Map<SubscriberName, CommandConstructor<any>[]> = new Map();
  private handlers: Map<SubscriberCallbackName, CommandHandlerWithOptions<any>> =
    new Map();
  private runOnceHandlersThisFrame: Map<
    SubscriberCallbackName,
    CommandHandlerWithData<any>
  > = new Map();
  private queues: Map<SubscriberName, CommandQueue<any>> = new Map();

  private sendHook: SendHook | undefined;
  private validateHook: ValidateHook | undefined;
  private checkIsCreateDisabledHook: CheckIsCreateDisabledHook | undefined;

  private debuggerIgnoreList?: CommandConstructor<any>[];
  private sendDebugger?: CommandServiceDebugger;
  private executeDebugger?: CommandServiceExecuteDebugger;

  public setHooks({
    sendHook,
    validateExecuteHook,
    checkIsCreateDisabledHook,
  }: {
    sendHook: SendHook;
    validateExecuteHook: ValidateHook;
    checkIsCreateDisabledHook: CheckIsCreateDisabledHook;
  }) {
    this.sendHook = sendHook;
    this.validateHook = validateExecuteHook;
    this.checkIsCreateDisabledHook = checkIsCreateDisabledHook;
  }

  public attachSendDebugger(debuggerFn: CommandServiceDebugger) {
    this.sendDebugger = debuggerFn;
  }
  public attachExecuteDebugger(debuggerFn: CommandServiceExecuteDebugger) {
    this.executeDebugger = debuggerFn;
  }
  public attachDebuggerIgnoreList(ignoreList: CommandConstructor<any>[]) {
    this.debuggerIgnoreList = ignoreList;
  }

  /**
   * Sends a command, which is queued and processed next frame by all subscribing systems.
   * Returns a promise that resolves after all subscribers have run and the event loop is unblocked,
   * which is after the end of the current frame / execute() call / requestAnimationFrame() call.
   *
   * @param CommandClass command to send
   * @param properties properties to include with the command
   * @returns a promise resolving to void
   */
  public async send<C extends BaseCommandProperties>(
    CommandClass: CommandConstructor<C>,
    properties: C = {} as C
  ): Promise<void> {
    /**
     * We should skip command execution if it is an entity creator command
     * and the board element limit is reached.
     */
    const shouldSkip =
      CommandClass.prototype instanceof CreateEntityCommand &&
      this.checkIsCreateDisabledHook?.();

    if (shouldSkip) {
      return;
    }
    const transformedProperties = this.sendHook?.(properties) as C;

    if (this.sendDebugger && this.shouldDebug(CommandClass)) {
      this.sendDebugger(CommandClass.name);
    }

    // Send this command to all currently subscribed queues
    const promises: Promise<void>[] = [];
    this.queues.forEach((queue, subscriberName) => {
      if (
        !this.checkExistingSubscription(subscriberName, CommandClass) ||
        !transformedProperties
      ) {
        return;
      }

      const deferrable = new Deferrable<void>();
      queue.enqueue({
        command: new CommandClass(transformedProperties),
        deferrable,
      });
      promises.push(deferrable.promise);
    });

    await Promise.all(promises);
  }

  private checkExistingSubscription<C extends BaseCommandProperties>(
    subscriberName: SubscriberName,
    CommandClass: CommandConstructor<C>
  ): boolean {
    const subscriber = this.subscriptions.get(subscriberName);
    if (!subscriber) {
      return false;
    }

    for (let i = 0; i < subscriber.length; i++) {
      if (subscriber[i] === CommandClass) {
        return true;
      }
    }

    return false;
  }

  public subscribe<C extends BaseCommandProperties>(
    commandClass: CommandConstructor<C>,
    commandHandler: (
      properties: InstanceType<CommandConstructor<C>>['properties']
    ) => void,
    subscriberName: SubscriberName,
    options?: SubscriptionOptions
  ) {
    if (this.checkExistingSubscription(subscriberName, commandClass)) {
      throwExistingSubscriberDevelopmentError(subscriberName, commandClass);
      return;
    }

    // Subscribe to all necessary commands.
    const commands = this.subscriptions.get(subscriberName) ?? [];
    commands.push(commandClass);
    this.subscriptions.set(subscriberName, commands);

    // Add handler for each command subscription
    const handlerId = this.getHandlerId(subscriberName, commandClass.name);

    this.handlers.set(handlerId, {
      handler: commandHandler,
      options,
    });

    // Create a queue in which commands will go for this subscriber only.
    // This should only happen once, after which commands are added to
    // the one queue for this system only.
    if (!this.queues.has(subscriberName)) {
      const queue = new CommandQueue<any>();
      this.queues.set(subscriberName, queue);
    }
  }

  public execute(subscriberName: SubscriberName) {
    const subscriptions = this.queues.get(subscriberName);
    if (!subscriptions) {
      throwNoSubscriptionsDevelopmentError(subscriberName);
      return;
    }

    // Clear the map of run once handlers for the frame, since we're about to run them
    // again
    if (this.runOnceHandlersThisFrame.size > 0) {
      this.runOnceHandlersThisFrame.clear();
    }

    subscriptions.forEachCommand(subscriberName, this.handleCommand);

    // Run all the handlers that should only be run once per frame
    if (this.runOnceHandlersThisFrame.size > 0) {
      for (const { handler, command } of this.runOnceHandlersThisFrame.values()) {
        this.executeHandlerWithDebugger(subscriberName, handler, command);
      }
    }
  }

  private handleCommand = (subscriberName: string, command: Command) => {
    const handlerId = this.getHandlerId(subscriberName, command.constructor.name);
    const handlerWithOptions = this.handlers.get(handlerId);
    if (!handlerWithOptions) {
      throw Error(
        `No handler registered for system ${subscriberName} under command ${command.constructor.name}`
      );
    }

    if (this.validateHook?.(command.properties)) {
      const { handler, options } = handlerWithOptions;
      if (options?.oncePerFrame) {
        // Don't run the handler immediately, but store it to be run later to ensure we
        // only run it once per frame
        this.runOnceHandlersThisFrame.set(handlerId, {
          handler,
          command,
        });
        return;
      }

      this.executeHandlerWithDebugger(subscriberName, handler, command);
    }
  };

  private executeHandlerWithDebugger(
    subscriberName: SubscriberName,
    handler: CommandHandler<any>,
    command: Command<any>
  ) {
    if (
      this.executeDebugger &&
      this.shouldDebug(command.constructor as CommandConstructor<any>)
    ) {
      this.executeDebugger(command.constructor.name, subscriberName);
    }

    handler(command.properties);
  }

  private getHandlerId(subscriberName: SubscriberName, commandName: string) {
    return subscriberName + ':' + commandName;
  }

  private shouldDebug(commandConstructor: CommandConstructor<any>) {
    if (!this.debuggerIgnoreList) {
      return true;
    }

    for (let i = 0; i < this.debuggerIgnoreList.length; i++) {
      const constructorToTest = this.debuggerIgnoreList[i];
      if (
        constructorToTest === commandConstructor ||
        constructorToTest.name === commandConstructor.name
      ) {
        return false;
      }
    }
    return true;
  }

  public unhandledCommandsCount() {
    let count = 0;

    for (const queue of this.queues.values()) {
      count += queue.size();
    }

    return count;
  }

  public dispose() {
    this.subscriptions.clear();
    this.handlers.clear();
    this.queues.clear();

    this.subscriptions = new Map();
    this.handlers = new Map();
    this.queues = new Map();
  }
}
